import React, { useState, useEffect, useContext, memo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faArrowRight,
  faCheck,
  faInfoCircle,
  faGift,
  faCalendarAlt,
} from '@fortawesome/free-solid-svg-icons';
import { Button } from '../ui/button';
import { AuthContext, useAuth } from '../../contexts/AuthContext';
import AddressSection from './AddressSection';
import DeliverySchedule from './DeliverySchedule';
import api from '../../lib/api';
import PaymentForm from './PaymentForm';
import PaymentStatus from './PaymentStatus';

// FrequencySection Component
const FrequencySection = ({
  deliveryFrequency,
  handleFrequencyChange,
  selectedDeliveryDays,
  setSelectedDeliveryDays,
  selectedTimeSlot,
  setSelectedTimeSlot,
}) => {
  // Days are indexed 0-6 (Monday-Sunday) to match backend
  const allWeekdays = [
    { id: 0, name: 'Monday' },
    { id: 1, name: 'Tuesday' },
    { id: 2, name: 'Wednesday' },
    { id: 3, name: 'Thursday' },
    { id: 4, name: 'Friday' },
    { id: 5, name: 'Saturday' },
    { id: 6, name: 'Sunday' },
  ];

  // Time slots from 6am to 9pm in 3-hour blocks
  const timeSlots = [
    { id: 1, time: '6:00 AM - 9:00 AM' },
    { id: 2, time: '9:00 AM - 12:00 PM' },
    { id: 3, time: '12:00 PM - 3:00 PM' },
    { id: 4, time: '3:00 PM - 6:00 PM' },
    { id: 5, time: '6:00 PM - 9:00 PM' },
  ];

  // Get current day of week and reorder days to start from tomorrow
  const today = new Date();
  const currentDayOfWeek = today.getDay(); // 0 is Sunday, 1 is Monday, etc.

  // Convert JavaScript day of week (0=Sunday) to our day numbering (0=Monday)
  const currentDayIndex = (currentDayOfWeek + 6) % 7;

  // Function to get date for a given weekday
  const getDateForWeekday = (weekdayId) => {
    const now = new Date();
    const todayDayIndex = (now.getDay() + 6) % 7; // Convert to our 0-6 (Mon-Sun) format

    let daysToAdd = weekdayId - todayDayIndex;

    // If it's today or already passed this week, show next week's date
    if (daysToAdd <= 0) {
      daysToAdd += 7;
    }

    const date = new Date(now);
    date.setDate(now.getDate() + daysToAdd);
    return date;
  };

  // Format date as "MMM D"
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Sort weekdays to start with tomorrow
  const getOrderedWeekdays = () => {
    const tomorrowIndex = (currentDayIndex + 1) % 7;

    // Start from tomorrow, then loop around
    const orderedWeekdays = [];
    for (let i = 0; i < 7; i++) {
      const dayIndex = (tomorrowIndex + i) % 7;
      const weekday = allWeekdays[dayIndex];

      if (weekday) {
        const date = getDateForWeekday(weekday.id);
        orderedWeekdays.push({
          ...weekday,
          date,
          formattedDate: formatDate(date),
        });
      }
    }

    return orderedWeekdays;
  };

  // Define weekdays with dates, sorted to start from tomorrow
  const weekdays = getOrderedWeekdays();

  const handleSelectDay = (dayId) => {
    // For frequency = 1, always replace the selected day
    if (deliveryFrequency === 1) {
      setSelectedDeliveryDays([dayId]);
      return;
    }

    // For higher frequencies (twice/thrice a week)
    if (selectedDeliveryDays.includes(dayId)) {
      // If user clicks on an already selected day, deselect it
      // But only if we won't go below the minimum required days
      if (selectedDeliveryDays.length > 1) {
        setSelectedDeliveryDays(
          selectedDeliveryDays.filter((id) => id !== dayId)
        );
      }
    } else {
      // If user clicks on an unselected day
      if (selectedDeliveryDays.length < deliveryFrequency) {
        // If we haven't reached the maximum number of days, add this day
        setSelectedDeliveryDays([...selectedDeliveryDays, dayId]);
      } else {
        // If we've reached the maximum, prompt user to deselect a day first
        // We could display a message, but for now we'll just do nothing
        // You could add an information message here if needed
      }
    }
  };

  // Handle time slot selection
  const handleSelectTimeSlot = (slotId) => {
    setSelectedTimeSlot(slotId);
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-3">Delivery Frequency</h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <button
          type="button"
          className={`px-4 py-3 border rounded-md text-center transition ${
            deliveryFrequency === 0
              ? 'bg-primary text-white border-primary'
              : 'bg-white text-gray-800 border-gray-300 hover:border-primary'
          }`}
          onClick={() => handleFrequencyChange(0)}
        >
          One-time
        </button>

        <button
          type="button"
          className={`px-4 py-3 border rounded-md text-center transition ${
            deliveryFrequency === 1
              ? 'bg-primary text-white border-primary'
              : 'bg-white text-gray-800 border-gray-300 hover:border-primary'
          }`}
          onClick={() => handleFrequencyChange(1)}
        >
          Once a week
          <div className="text-xs mt-1 font-medium text-green-500">20% off</div>
        </button>

        <button
          type="button"
          className={`px-4 py-3 border rounded-md text-center transition ${
            deliveryFrequency === 2
              ? 'bg-primary text-white border-primary'
              : 'bg-white text-gray-800 border-gray-300 hover:border-primary'
          }`}
          onClick={() => handleFrequencyChange(2)}
        >
          Twice a week
          <div className="text-xs mt-1 font-medium text-green-500">25% off</div>
        </button>

        <button
          type="button"
          className={`px-4 py-3 border rounded-md text-center transition ${
            deliveryFrequency === 3
              ? 'bg-primary text-white border-primary'
              : 'bg-white text-gray-800 border-gray-300 hover:border-primary'
          }`}
          onClick={() => handleFrequencyChange(3)}
        >
          Thrice a week
          <div className="text-xs mt-1 font-medium text-green-500">30% off</div>
        </button>
      </div>

      {deliveryFrequency > 0 && (
        <div className="mb-4">
          <h4 className="font-medium text-gray-700 mb-2">Delivery Days</h4>
          <p className="text-sm text-gray-600 mb-3">
            Please select {deliveryFrequency} day
            {deliveryFrequency > 1 ? 's' : ''} for your deliveries:
          </p>

          <div className="flex flex-wrap gap-2">
            {weekdays.map((day) => (
              <button
                key={day.id}
                type="button"
                onClick={() => handleSelectDay(day.id)}
                className={`px-4 py-3 border rounded-md text-sm ${
                  selectedDeliveryDays.includes(day.id)
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-gray-800 border-gray-300 hover:border-primary'
                }`}
                disabled={
                  !selectedDeliveryDays.includes(day.id) &&
                  selectedDeliveryDays.length >= deliveryFrequency &&
                  deliveryFrequency !== 1 // Never disable when frequency is 1 (single day selection)
                }
              >
                <div className="flex flex-col items-center">
                  <span className="font-medium">{day.name}</span>
                  <span className="text-xs mt-1">{day.formattedDate}</span>
                </div>
              </button>
            ))}
          </div>

          <div className="mt-2 text-xs text-gray-500 flex items-center">
            <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
            You need to select exactly {deliveryFrequency} day
            {deliveryFrequency > 1 ? 's' : ''} for delivery
          </div>

          <h4 className="font-medium text-gray-700 mt-5 mb-2">Delivery Time</h4>
          <p className="text-sm text-gray-600 mb-3">
            Please select your preferred delivery time slot:
          </p>

          <div className="flex flex-wrap gap-2">
            {timeSlots.map((slot) => (
              <button
                key={slot.id}
                type="button"
                onClick={() => handleSelectTimeSlot(slot.id)}
                className={`px-3 py-2 border rounded-md text-sm ${
                  selectedTimeSlot === slot.id
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-gray-800 border-gray-300 hover:border-primary'
                }`}
              >
                {slot.time}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// CostBreakdown Component
const CostBreakdown = ({
  deliveryFrequency,
  calculateWeeklyTotalWithoutPromoAndDelivery,
  calculateDeliveryCharge,
  getTotalDeliveryCharge,
  calculateWeeklyTotal,
  calculateMonthlyTotal,
  calculateAmountForFreeDelivery,
  promoCode,
  setPromoCode,
  appliedPromo,
  promoMessage,
  showPromoInput,
  setShowPromoInput,
  handleApplyPromoCode,
  handleRemovePromo,
}) => {
  // Calculate values for detailed breakdown
  const getDeliveryCount = () => {
    switch (deliveryFrequency) {
      case 0:
        return 1; // One-time
      case 1:
        return 4; // Once a week for 1 month = 4 deliveries
      case 2:
        return 8; // Twice a week for 1 month = 8 deliveries
      case 3:
        return 12; // Thrice a week for 1 month = 12 deliveries
      default:
        return 4;
    }
  };

  const getSubscriptionWeeks = () => {
    return deliveryFrequency > 0 ? 4 : 1; // 4 weeks for subscription, 1 for one-time
  };

  // Get discount percentage based on frequency
  const getDiscountPercentage = () => {
    const discountRates = [0, 20, 25, 30]; // Discount percentages for each delivery frequency
    return discountRates[deliveryFrequency] || 0;
  };

  // Calculate per-delivery cost
  const getBaseDeliveryCost = () => {
    if (deliveryFrequency === 0) {
      return (
        calculateWeeklyTotal() / (1 - (appliedPromo?.discount || 0)) -
        calculateDeliveryCharge()
      );
    } else {
      const weeklySubtotal = calculateWeeklyTotalWithoutPromoAndDelivery();
      return weeklySubtotal / deliveryFrequency;
    }
  };

  // Calculate discounted per-delivery cost
  const getDiscountedDeliveryCost = () => {
    const baseDeliveryCost = getBaseDeliveryCost();
    const discountPercentage = getDiscountPercentage();
    return baseDeliveryCost * (1 - discountPercentage / 100);
  };

  // Shows standard retail price (without subscription discount)
  const getRetailPricePerDelivery = () => {
    if (deliveryFrequency === 0) {
      return getBaseDeliveryCost();
    } else {
      // For subscriptions, we show what it would cost without subscription discount
      return getBaseDeliveryCost() / (1 - getDiscountPercentage() / 100);
    }
  };

  // Get formatted discount text
  const getDiscountText = () => {
    const discount = getDiscountPercentage();
    return discount > 0 ? `${discount}%` : 'None';
  };

  return (
    <div className="bg-gray-50 border border-gray-200 p-4 rounded-md">
      {/* Subscription Details Card - Only show for subscription plans */}
      {deliveryFrequency > 0 && (
        <div className="mb-4 bg-white border border-gray-200 rounded-md p-3">
          <h4 className="font-medium text-gray-800 mb-2 flex items-center">
            <FontAwesomeIcon
              icon={faCalendarAlt}
              className="mr-2 text-primary"
            />
            Subscription Details
          </h4>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-gray-600">Subscription Duration:</p>
              <p className="font-medium">4 weeks (1 month)</p>
            </div>
            <div>
              <p className="text-gray-600">Deliveries per Week:</p>
              <p className="font-medium">{deliveryFrequency}</p>
            </div>
            <div>
              <p className="text-gray-600">Total Deliveries:</p>
              <p className="font-medium">{getDeliveryCount()}</p>
            </div>
            <div>
              <p className="text-gray-600">Subscription Discount:</p>
              <p className="font-medium text-green-600">{getDiscountText()}</p>
            </div>
          </div>
        </div>
      )}

      {/* Pricing breakdown */}
      {deliveryFrequency > 0 ? (
        <>
          {/* Subscription pricing */}
          <div className="mb-3">
            <h4 className="font-medium text-gray-800 mb-2">Price Breakdown</h4>

            <div className="bg-white border border-gray-200 rounded-md p-3 mb-3">
              <div className="flex justify-between py-1.5">
                <span className="text-gray-600">
                  Standard price per delivery:
                </span>
                <span className="font-medium">
                  ₹{getRetailPricePerDelivery().toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between py-1.5">
                <span className="text-gray-600">Subscription discount:</span>
                <span className="text-green-600">-{getDiscountText()}</span>
              </div>

              <div className="flex justify-between py-1.5 border-t border-gray-100">
                <span className="text-gray-700 font-medium">
                  Your price per delivery:
                </span>
                <span className="font-semibold">
                  ₹{getDiscountedDeliveryCost().toFixed(2)}
                </span>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-md p-3">
              <div className="flex justify-between py-1.5">
                <span className="text-gray-600">Price per delivery:</span>
                <span className="font-medium">
                  ₹{getDiscountedDeliveryCost().toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between py-1.5">
                <span className="text-gray-600">× Deliveries per week:</span>
                <span className="font-medium">{deliveryFrequency}</span>
              </div>

              <div className="flex justify-between py-1.5 border-t border-gray-100">
                <span className="text-gray-700 font-medium">
                  Weekly subtotal:
                </span>
                <span className="font-semibold">
                  ₹{calculateWeeklyTotalWithoutPromoAndDelivery().toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between py-1.5">
                <span className="text-gray-600">× Number of weeks:</span>
                <span className="font-medium">{getSubscriptionWeeks()}</span>
              </div>

              <div className="flex justify-between py-1.5 border-t border-gray-100">
                <span className="text-gray-700 font-medium">
                  Monthly subtotal:
                </span>
                <span className="font-semibold">
                  ₹
                  {(calculateWeeklyTotalWithoutPromoAndDelivery() * 4).toFixed(
                    2
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-between py-2 border-b border-gray-200">
            <span className="text-gray-600">Delivery Charge:</span>
            <span>
              {calculateDeliveryCharge() === 0 ? (
                <>
                  <span className="line-through text-gray-400 mr-2">
                    ₹35.00 × {deliveryFrequency} × 4 weeks
                  </span>
                  <span className="text-green-600 font-medium text-sm">
                    FREE
                  </span>
                </>
              ) : (
                <>
                  {`₹35 × ${deliveryFrequency} × 4 weeks = ₹${(
                    getTotalDeliveryCharge() * 4
                  ).toFixed(2)}`}
                </>
              )}
            </span>
          </div>

          {calculateDeliveryCharge() > 0 && (
            <div className="flex items-center bg-green-50 border-l-2 border-green-500 p-2 my-2 text-sm">
              <FontAwesomeIcon icon={faGift} className="text-green-500 mr-2" />
              <span>
                Add products worth{' '}
                <strong className="text-green-700">
                  ₹{calculateAmountForFreeDelivery().toFixed(2)}
                </strong>{' '}
                more to get{' '}
                <strong className="text-green-700">
                  FREE delivery on all shipments!
                </strong>
              </span>
            </div>
          )}

          <div className="flex justify-between py-2 border-b border-gray-200">
            <div className="flex items-center flex-wrap">
              <span className="text-gray-600">Promo Discount:</span>
              {!appliedPromo && !showPromoInput && (
                <button
                  type="button"
                  onClick={() => setShowPromoInput(true)}
                  className="ml-2 text-primary text-sm underline"
                >
                  Add promo code
                </button>
              )}

              {showPromoInput && (
                <div className="flex items-center ml-2 mt-1">
                  <input
                    type="text"
                    placeholder="Enter code"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    className="px-2 py-1 border border-gray-300 text-sm w-24"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleApplyPromoCode();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleApplyPromoCode}
                    className="ml-1 px-2 py-1 bg-primary text-white text-sm"
                  >
                    Apply
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPromoInput(false);
                    }}
                    className="ml-1 text-gray-500 text-sm underline"
                  >
                    Cancel
                  </button>
                </div>
              )}

              {appliedPromo && !showPromoInput && (
                <div className="flex items-center ml-2">
                  <span className="bg-green-50 text-green-800 text-xs px-2 py-0.5 rounded">
                    {promoCode.toUpperCase()}
                  </span>
                  <button
                    type="button"
                    onClick={handleRemovePromo}
                    className="ml-1 text-gray-500 text-xs underline"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
            <span>
              {appliedPromo
                ? `-₹${(
                    (calculateWeeklyTotal() / (1 - appliedPromo.discount)) *
                    appliedPromo.discount *
                    4
                  ).toFixed(2)}`
                : '₹0.00'}
            </span>
          </div>

          {promoMessage && showPromoInput && (
            <div
              className={`text-sm mt-1 mb-2 ${
                appliedPromo ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {promoMessage}
            </div>
          )}

          <div className="flex justify-between py-2 border-b border-gray-200">
            <span className="text-gray-600">Weekly Total:</span>
            <span className="font-medium">
              ₹{calculateWeeklyTotal().toFixed(2)}
            </span>
          </div>

          <div className="flex justify-between py-2 font-semibold text-gray-800">
            <span>Monthly Charge (4 weeks):</span>
            <span className="text-lg">
              ₹{calculateMonthlyTotal().toFixed(2)}
            </span>
          </div>

          <div className="mt-2 text-xs text-gray-500 flex items-center">
            <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
            Your subscription will be billed monthly at ₹
            {calculateMonthlyTotal().toFixed(2)}
          </div>
        </>
      ) : (
        <>
          {/* One-time purchase pricing */}
          <div className="mb-3">
            <h4 className="font-medium text-gray-800 mb-2">
              One-Time Purchase
            </h4>

            <div className="bg-white border border-gray-200 rounded-md p-3">
              <div className="flex justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">
                  ₹
                  {(
                    calculateWeeklyTotal() /
                      (1 - (appliedPromo?.discount || 0)) -
                    calculateDeliveryCharge()
                  ).toFixed(2)}
                </span>
              </div>

              <div className="flex justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">Delivery Charge:</span>
                <span>
                  {calculateDeliveryCharge() === 0 ? (
                    <>
                      <span className="line-through text-gray-400 mr-2">
                        ₹35.00
                      </span>
                      <span className="text-green-600 font-medium text-sm">
                        FREE
                      </span>
                    </>
                  ) : (
                    `₹${calculateDeliveryCharge().toFixed(2)}`
                  )}
                </span>
              </div>
            </div>
          </div>

          {calculateDeliveryCharge() > 0 && (
            <div className="flex items-center bg-green-50 border-l-2 border-green-500 p-2 my-2 text-sm">
              <FontAwesomeIcon icon={faGift} className="text-green-500 mr-2" />
              <span>
                Add products worth{' '}
                <strong className="text-green-700">
                  ₹{calculateAmountForFreeDelivery().toFixed(2)}
                </strong>{' '}
                more to get{' '}
                <strong className="text-green-700">FREE delivery!</strong>
              </span>
            </div>
          )}

          <div className="flex justify-between py-2 border-b border-gray-200">
            <div className="flex items-center flex-wrap">
              <span className="text-gray-600">Promo Discount:</span>
              {!appliedPromo && !showPromoInput && (
                <button
                  type="button"
                  onClick={() => setShowPromoInput(true)}
                  className="ml-2 text-primary text-sm underline"
                >
                  Add promo code
                </button>
              )}

              {showPromoInput && (
                <div className="flex items-center ml-2 mt-1">
                  <input
                    type="text"
                    placeholder="Enter code"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    className="px-2 py-1 border border-gray-300 text-sm w-24"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleApplyPromoCode();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleApplyPromoCode}
                    className="ml-1 px-2 py-1 bg-primary text-white text-sm"
                  >
                    Apply
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPromoInput(false);
                    }}
                    className="ml-1 text-gray-500 text-sm underline"
                  >
                    Cancel
                  </button>
                </div>
              )}

              {appliedPromo && !showPromoInput && (
                <div className="flex items-center ml-2">
                  <span className="bg-green-50 text-green-800 text-xs px-2 py-0.5 rounded">
                    {promoCode.toUpperCase()}
                  </span>
                  <button
                    type="button"
                    onClick={handleRemovePromo}
                    className="ml-1 text-gray-500 text-xs underline"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
            <span>
              {appliedPromo
                ? `-₹${(
                    (calculateWeeklyTotalWithoutPromoAndDelivery() /
                      (1 - appliedPromo.discount)) *
                    appliedPromo.discount
                  ).toFixed(2)}`
                : '₹0.00'}
            </span>
          </div>

          {promoMessage && showPromoInput && (
            <div
              className={`text-sm mt-1 mb-2 ${
                appliedPromo ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {promoMessage}
            </div>
          )}

          <div className="flex justify-between py-2 font-semibold text-gray-800">
            <span>Total:</span>
            <span className="text-lg">
              ₹{calculateWeeklyTotal().toFixed(2)}
            </span>
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-md p-3 mt-3">
            <p className="text-sm text-blue-700">
              <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
              Subscribe to a weekly delivery plan and save up to 30% on every
              order!
            </p>
          </div>
        </>
      )}
    </div>
  );
};

// Main Subscription Panel Component
const SubscriptionPanel = memo(
  ({
    selectedFlowers,
    onClose,
    calculateWeeklyTotal,
    calculateMonthlyTotal,
  }) => {
    const { currentUser, getUserAddresses, addAddress, token } =
      useContext(AuthContext);
    // No PaymentContext dependency
    const [deliveryFrequency, setDeliveryFrequency] = useState(1);
    const [selectedDeliveryDays, setSelectedDeliveryDays] = useState([0]); // Default to Monday (id: 0)
    const [selectedTimeSlot, setSelectedTimeSlot] = useState(3); // Default to 12PM-3PM (mid-day)
    const [addresses, setAddresses] = useState([]);
    const [selectedAddressId, setSelectedAddressId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    // Payment and step management
    const [currentStep, setCurrentStep] = useState('details'); // details, payment, success
    const [createdSubscription, setCreatedSubscription] = useState(null);
    const [paymentId, setPaymentId] = useState(null);
    const [predictedDeliveries, setPredictedDeliveries] = useState([]);
    const [promoCode, setPromoCode] = useState('');
    const [appliedPromo, setAppliedPromo] = useState(null);
    const [promoMessage, setPromoMessage] = useState('');
    const [showPromoInput, setShowPromoInput] = useState(false);

    // State for address form
    const [showAddressForm, setShowAddressForm] = useState(false);
    const [newAddress, setNewAddress] = useState({
      recipient: currentUser?.name || '',
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'India',
      phone: currentUser?.phone || '',
      is_default: false, // Default to false initially, will set based on if it's first address
    });
    const [addressErrors, setAddressErrors] = useState({});
    const [isSavingAddress, setIsSavingAddress] = useState(false);

    // Sample promo codes (in a real app, these would be validated on the server)
    const promoCodes = {
      WELCOME20: { discount: 0.2, message: '20% off your order!' },
      LUXURY10: { discount: 0.1, message: '10% discount applied!' },
      FLOWERS50: {
        discount: 0.25,
        message: 'Enjoy 25% off your subscription!',
      },
    };

    // Function to fetch and update addresses
    const fetchAddresses = async () => {
      try {
        const addressData = await getUserAddresses();

        // Only update state if component is still mounted and data is valid
        if (addressData && Array.isArray(addressData)) {
          setAddresses(addressData);

          // Set default address if available
          const defaultAddress = addressData.find((addr) => addr.is_default);
          if (defaultAddress) {
            setSelectedAddressId(defaultAddress.id);
          } else if (addressData.length > 0) {
            setSelectedAddressId(addressData[0].id);
          }
        }
      } catch (err) {
        console.error('Error fetching addresses:', err);
        setError(
          'Failed to load addresses. Please add a delivery address below.'
        );
      }
    };

    // Fetch user addresses on component mount or when auth state changes
    useEffect(() => {
      // Check both currentUser and token to ensure we're fully authenticated
      if (currentUser && token) {
        fetchAddresses();
      }
    }, [currentUser, getUserAddresses, token]); // Add token as a dependency

    // Validate address form
    const validateAddressForm = () => {
      const errors = {};

      if (!newAddress.recipient.trim()) {
        errors.recipient = 'Recipient name is required';
      }

      if (!newAddress.line1.trim()) {
        errors.line1 = 'Address line 1 is required';
      }

      if (!newAddress.city.trim()) {
        errors.city = 'City is required';
      }

      if (!newAddress.state.trim()) {
        errors.state = 'State is required';
      }

      if (!newAddress.postal_code.trim()) {
        errors.postal_code = 'Postal code is required';
      }

      if (!newAddress.country.trim()) {
        errors.country = 'Country is required';
      }

      if (!newAddress.phone.trim()) {
        errors.phone = 'Phone number is required';
      } else if (!/^\+?[0-9]{10,15}$/.test(newAddress.phone)) {
        errors.phone = 'Please enter a valid phone number';
      }

      setAddressErrors(errors);
      return Object.keys(errors).length === 0;
    };

    // Save new address
    const handleSaveAddress = async () => {
      if (!validateAddressForm()) {
        return;
      }

      setIsSavingAddress(true);
      setError(null);

      try {
        // Prepare address data
        const addressData = {
          ...newAddress,
          user_id: currentUser?.id,
        };

        // Use the addAddress function from AuthContext to save the address
        const newAddressWithId = await addAddress(addressData);

        // Add the new address to the addresses list and select it
        setAddresses([...addresses, newAddressWithId]);
        setSelectedAddressId(newAddressWithId.id);

        // Hide the address form
        setShowAddressForm(false);

        // Reset the form
        setNewAddress({
          recipient: '',
          line1: '',
          line2: '',
          city: '',
          state: '',
          postal_code: '',
          country: 'India',
          phone: '',
          is_default: false, // Reset to false by default
        });
      } catch (err) {
        console.error('Error saving address:', err);
        setError(
          err.response?.data?.detail ||
            'Failed to save address. Please try again.'
        );
      } finally {
        setIsSavingAddress(false);
      }
    };

    // Functions for pricing and delivery charges
    const calculateWeeklyTotalWithoutPromoAndDelivery = () => {
      const discountRates = [0, 0.2, 0.25, 0.3]; // Discount rates for each delivery frequency
      const discount = discountRates[deliveryFrequency] || 0;
      const total = selectedFlowers.reduce(
        (total, flower) =>
          total +
          flower.pricePerStem *
            flower.quantity *
            (deliveryFrequency > 0 ? deliveryFrequency : 1),
        0
      );
      return (total / (1 - discountRates[1])) * (1 - discount);
    };

    const calculateDeliveryCharge = () => {
      const subtotal = calculateWeeklyTotalWithoutPromoAndDelivery();
      return subtotal < 499 ? 35 : 0;
    };

    const getTotalDeliveryCharge = () => {
      const baseCharge = calculateDeliveryCharge();
      if (deliveryFrequency === 0 || baseCharge === 0) return baseCharge;
      return baseCharge * deliveryFrequency;
    };

    const calculateAmountForFreeDelivery = () => {
      const subtotal = calculateWeeklyTotalWithoutPromoAndDelivery();
      return Math.max(0, 499 - subtotal);
    };

    // Apply promo discount to the total
    const applyPromoDiscount = (price) => {
      if (appliedPromo) {
        return price * (1 - appliedPromo.discount);
      }
      return price;
    };

    // Calculate total based on selected flowers and delivery frequency
    const calculateWeeklyTotalFunc = () => {
      const discountRates = [0, 0.2, 0.25, 0.3]; // Discount rates for each delivery frequency
      const discount = discountRates[deliveryFrequency] || 0;
      const total = selectedFlowers.reduce(
        (total, flower) =>
          total +
          flower.pricePerStem *
            flower.quantity *
            (deliveryFrequency > 0 ? deliveryFrequency : 1),
        0
      );
      const weeklySubtotal = (total / (1 - discountRates[1])) * (1 - discount);
      const deliveryCharge = getTotalDeliveryCharge();
      const weeklyTotalWithDelivery = weeklySubtotal + deliveryCharge;

      return applyPromoDiscount(weeklyTotalWithDelivery);
    };

    const calculateMonthlyTotalFunc = () => {
      const weeklyTotal = calculateWeeklyTotalFunc();
      return weeklyTotal * deliveryFrequency * 4; // 4 weeks in a month
    };

    // Generate predicted delivery schedule based on current selections
    useEffect(() => {
      // Only generate predictions if we have delivery days selected
      if (deliveryFrequency === 0 || selectedDeliveryDays.length === 0) {
        if (deliveryFrequency === 0) {
          // One-time delivery is scheduled for next day
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          setPredictedDeliveries([
            {
              id: 'predicted-one-time',
              status: 'scheduled',
              scheduled_date: tomorrow.toISOString(),
              delivered_date: null,
            },
          ]);
        } else {
          setPredictedDeliveries([]);
        }
        return;
      }

      // Create predicted deliveries based on frequency and selected days
      const predictions = [];
      const startDate = new Date();
      const deliveryCount = getDeliveryCount();

      // Map day IDs (1-6) to JavaScript day of week (0-6, where 0 is Sunday)
      // Note: Our API uses 0=Monday, 6=Sunday while JS Date uses 0=Sunday, 6=Saturday
      const mapDayIdToJsDay = (dayId) => (dayId % 7) + 1;

      let currentDate = new Date(startDate);
      let generatedCount = 0;

      while (generatedCount < deliveryCount) {
        for (let dayId of selectedDeliveryDays) {
          if (generatedCount >= deliveryCount) break;

          const targetJsDay = mapDayIdToJsDay(dayId);
          const currentJsDay = currentDate.getDay();
          const daysToAdd = (targetJsDay - currentJsDay + 7) % 7;

          // If we're on the day itself, we want next week's occurrence
          const deliveryDate = new Date(currentDate);
          deliveryDate.setDate(
            currentDate.getDate() + (daysToAdd === 0 ? 7 : daysToAdd)
          );

          predictions.push({
            id: `predicted-${generatedCount}`,
            status: 'scheduled',
            scheduled_date: deliveryDate.toISOString(),
            delivered_date: null,
          });

          currentDate = new Date(deliveryDate);
          generatedCount++;
        }

        // If we haven't generated enough yet, move to the next day for next iteration
        if (generatedCount < deliveryCount) {
          currentDate.setDate(currentDate.getDate() + 1);
        }
      }

      setPredictedDeliveries(predictions);
    }, [deliveryFrequency, selectedDeliveryDays]);

    // Handle frequency change
    const handleFrequencyChange = (value) => {
      setDeliveryFrequency(value);

      // Initialize selected days based on frequency
      if (value === 0) {
        setSelectedDeliveryDays([]);
      } else if (value === 1) {
        if (selectedDeliveryDays.length !== 1) {
          setSelectedDeliveryDays([0]); // Default to Monday (id: 0)
        }
      } else if (value === 2) {
        if (selectedDeliveryDays.length !== 2) {
          setSelectedDeliveryDays([0, 3]); // Default to Monday and Thursday
        }
      } else if (value === 3) {
        if (selectedDeliveryDays.length !== 3) {
          setSelectedDeliveryDays([0, 2, 4]); // Default to Monday, Wednesday, and Friday
        }
      }
    };

    // Handle promo code application
    const handleApplyPromoCode = (e) => {
      if (e) e.preventDefault();

      const trimmedCode = promoCode.trim().toUpperCase();

      if (!trimmedCode) {
        setPromoMessage('Please enter a promo code');
        setAppliedPromo(null);
        return;
      }

      if (promoCodes[trimmedCode]) {
        setAppliedPromo(promoCodes[trimmedCode]);
        setPromoMessage(promoCodes[trimmedCode].message);
        setShowPromoInput(false); // Hide input after successful application
      } else {
        setPromoMessage('Invalid promo code');
        setAppliedPromo(null);
      }
    };

    // Handle removing promo code
    const handleRemovePromo = () => {
      setAppliedPromo(null);
      setPromoCode('');
      setPromoMessage('');
    };

    // Determine delivery count based on frequency and subscription duration (1-month)
    const getDeliveryCount = () => {
      switch (deliveryFrequency) {
        case 0:
          return 1; // One-time
        case 1:
          return 4; // Once a week for 1 month = 4 deliveries
        case 2:
          return 8; // Twice a week for 1 month = 8 deliveries
        case 3:
          return 12; // Thrice a week for 1 month = 12 deliveries
        default:
          return 4;
      }
    };

    // No cleanup needed without PaymentContext

    // Handle form submission to create subscription
    const handleCreateSubscription = async (e) => {
      if (e) e.preventDefault();

      // Validate selections
      if (selectedFlowers.length === 0) {
        setError('Please select at least one flower');
        return;
      }

      if (
        deliveryFrequency > 0 &&
        selectedDeliveryDays.length < deliveryFrequency
      ) {
        setError(`Please select ${deliveryFrequency} delivery days`);
        return;
      }

      if (!selectedAddressId && currentUser) {
        setError('Please add a delivery address using the form above');
        setShowAddressForm(true);
        return;
      }

      if (!currentUser) {
        setError('Please log in to create a subscription');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Prepare subscription data
        const subscriptionData = {
          user_id: currentUser?.id,
          status: 'pending', // Set initial status as pending until payment completes
          duration: deliveryFrequency === 0 ? 'one-time' : '1-month',
          delivery_count: getDeliveryCount(),
          delivery_days: selectedDeliveryDays,
          delivery_time_slot: selectedTimeSlot,
          total_amount: calculateWeeklyTotalFunc(),
          start_date: new Date().toISOString(),
          end_date: null,
          address_id: selectedAddressId,
          items: selectedFlowers.map((flower) => ({
            flower_id: flower.id,
            stem_count: flower.quantity,
            price: flower.pricePerStem * flower.quantity,
          })),
        };

        // Create subscription using axios instance from api.js with proper JWT token
        const response = await api.post('subscriptions', subscriptionData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        const subscription = response.data;
        setCreatedSubscription(subscription);

        // Proceed to payment step
        setCurrentStep('payment');

        // Create order for payment
        await handleCreateOrder(subscription.id);
      } catch (error) {
        console.error('Error creating subscription:', error);
        setError(
          error.response?.data?.detail ||
            'Failed to create subscription. Please try again.'
        );
      } finally {
        setIsLoading(false);
      }
    };

    // Create payment order directly via API
    const handleCreateOrder = async (subscriptionId) => {
      try {
        setIsLoading(true);
        console.log('Creating order for subscription:', subscriptionId);
        console.log('Auth token available:', !!token);

        // Calculate amount based on frequency
        const amount =
          deliveryFrequency === 0
            ? calculateWeeklyTotalFunc()
            : calculateMonthlyTotalFunc();

        console.log('Payment amount:', amount);

        // Create order directly using API call
        console.log('Making API call to create order...');
        const response = await api.post(
          'payments/create-order',
          {
            subscription_id: subscriptionId,
            amount: amount,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        console.log('Order creation response:', response.data);

        if (!response.data) {
          console.error('No data returned from order creation');
          throw new Error('Failed to create payment order');
        }

        // Always use Razorpay's order ID, not our internal database ID
        const razorpayOrderId = response.data.razorpay_order_id;

        // Verify we have a valid Razorpay order ID (typically starting with "order_")
        if (!razorpayOrderId) {
          console.error(
            'No Razorpay order ID found in the response:',
            response.data
          );
          throw new Error('Missing Razorpay order ID from server');
        }

        console.log(
          'Setting payment ID to Razorpay order ID:',
          razorpayOrderId
        );
        setPaymentId(razorpayOrderId);
        setIsLoading(false);
        return response.data;
      } catch (err) {
        console.error('Error creating payment order:', err);
        setError('Failed to initialize payment. Please try again.');
        setIsLoading(false);
        return null;
      }
    };

    // Verify payment with API directly instead of using PaymentContext
    const verifyPayment = async (paymentResponse) => {
      try {
        const response = await api.post(
          'payments/verify',
          {
            razorpay_order_id: paymentResponse.razorpay_order_id,
            razorpay_payment_id: paymentResponse.razorpay_payment_id,
            razorpay_signature: paymentResponse.razorpay_signature,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        return response.data.success;
      } catch (err) {
        console.error('Payment verification error:', err);
        throw err;
      }
    };

    // Handle payment success
    const handlePaymentSuccess = async (paymentResponse) => {
      try {
        setIsLoading(true);

        // Verify payment with backend
        const verified = await verifyPayment(paymentResponse);

        if (verified) {
          // Payment was successful, update UI
          setCurrentStep('success');
          setSuccess(true);
        } else {
          // Payment verification failed
          setError('Payment verification failed. Please try again.');
        }
      } catch (err) {
        console.error('Payment verification error:', err);
        setError(
          'Failed to complete payment. Please contact customer support.'
        );
      } finally {
        setIsLoading(false);
      }
    };

    // Handle payment error
    const handlePaymentError = (err) => {
      console.error('Payment error:', err);
      setError(
        typeof err === 'string' ? err : 'Payment failed. Please try again.'
      );
    };

    // Try payment again (after failure)
    const handleRetryPayment = () => {
      if (createdSubscription) {
        handleCreateOrder(createdSubscription.id);
      }
    };

    // Effect to redirect after successful payment
    useEffect(() => {
      if (currentStep === 'success' || success) {
        // Show success message briefly before redirecting
        const redirectTimer = setTimeout(() => {
          // Use simple window.location for navigation
          window.location.href = '/profile';
        }, 2000); // 2 second delay to let user see the success message

        return () => clearTimeout(redirectTimer);
      }
    }, [currentStep, success]);

    // Render based on current step
    if (currentStep === 'success' || success) {
      return (
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                ></path>
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-gray-800">
              Payment Successful!
            </h2>
            <p className="text-gray-600 mt-2">
              Your flower subscription has been successfully created and payment
              processed. We'll deliver your beautiful flowers according to your
              selected schedule.
            </p>
            <p className="text-primary mt-4">
              Redirecting to your subscription details...
            </p>
          </div>
          <div className="mt-6 text-center">
            <Button onClick={() => (window.location.href = '/profile')}>
              View Subscription Details
            </Button>
          </div>
        </div>
      );
    }

    // Payment step
    if (currentStep === 'payment') {
      return (
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          >
            <FontAwesomeIcon icon={faTimes} size="lg" />
          </button>

          <h2 className="text-2xl font-bold mb-6 text-center">
            Complete Your Payment
          </h2>

          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : paymentId ? (
            <PaymentForm
              subscriptionId={createdSubscription?.id}
              amount={
                deliveryFrequency === 0
                  ? calculateWeeklyTotalFunc()
                  : calculateMonthlyTotalFunc()
              }
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
              customerData={{
                name: currentUser?.name,
                email: currentUser?.email,
                phone: currentUser?.phone,
              }}
              existingOrderId={paymentId}
            />
          ) : (
            <div className="text-center py-6">
              <p className="text-red-600 mb-4">Failed to initialize payment</p>
              <Button onClick={handleRetryPayment}>Try Again</Button>
            </div>
          )}

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => setCurrentStep('details')}
              className="text-gray-500 underline text-sm"
            >
              Back to subscription details
            </button>
          </div>
        </div>
      );
    }

    // Default 'details' step
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
        >
          <FontAwesomeIcon icon={faTimes} size="lg" />
        </button>

        <h2 className="text-2xl font-bold mb-6 text-center">
          Complete Your Subscription
        </h2>

        {error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md">
            {error}
          </div>
        )}

        <form onSubmit={handleCreateSubscription}>
          {/* Delivery Frequency Section */}
          <div className="mb-4">
            <FrequencySection
              deliveryFrequency={deliveryFrequency}
              handleFrequencyChange={handleFrequencyChange}
              selectedDeliveryDays={selectedDeliveryDays}
              setSelectedDeliveryDays={setSelectedDeliveryDays}
              selectedTimeSlot={selectedTimeSlot}
              setSelectedTimeSlot={setSelectedTimeSlot}
            />
          </div>

          {/* Delivery Schedule Preview - Moved up to appear right after frequency selection */}
          {deliveryFrequency > 0 && (
            <div className="mb-6 mt-2">
              <div className="bg-gray-50 border border-gray-200 p-3 rounded-md">
                <DeliverySchedule
                  deliveries={predictedDeliveries}
                  loading={false}
                  error={null}
                  selectedTimeSlot={selectedTimeSlot}
                  selectedDeliveryDays={selectedDeliveryDays}
                />
              </div>
            </div>
          )}

          {deliveryFrequency === 0 && (
            <div className="mb-6 mt-2">
              <div className="bg-gray-50 border border-gray-200 p-3 rounded-md">
                <p className="text-sm text-gray-700 mb-1 font-medium">
                  Delivery Information
                </p>
                <p className="text-sm text-gray-600">
                  Your one-time delivery will be scheduled for tomorrow.
                </p>
              </div>
            </div>
          )}

          {/* Address Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4">Delivery Address</h3>
            <AddressSection
              addresses={addresses}
              selectedAddressId={selectedAddressId}
              setSelectedAddressId={setSelectedAddressId}
              showAddressForm={showAddressForm}
              setShowAddressForm={(show) => {
                // If showing the form and no addresses exist, set is_default to true
                if (show && addresses.length === 0) {
                  setNewAddress({ ...newAddress, is_default: true });
                }
                setShowAddressForm(show);
              }}
              newAddress={newAddress}
              setNewAddress={setNewAddress}
              addressErrors={addressErrors}
              handleSaveAddress={handleSaveAddress}
              isSavingAddress={isSavingAddress}
              error={error}
              onAuthSuccess={() => {
                // Trigger address fetch after successful auth
                if (currentUser && token) {
                  fetchAddresses();
                }
              }}
            />
          </div>

          {/* Cost Breakdown */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4">Order Summary</h3>
            <CostBreakdown
              deliveryFrequency={deliveryFrequency}
              calculateWeeklyTotalWithoutPromoAndDelivery={
                calculateWeeklyTotalWithoutPromoAndDelivery
              }
              calculateDeliveryCharge={calculateDeliveryCharge}
              getTotalDeliveryCharge={getTotalDeliveryCharge}
              calculateWeeklyTotal={calculateWeeklyTotalFunc}
              calculateMonthlyTotal={calculateMonthlyTotalFunc}
              calculateAmountForFreeDelivery={calculateAmountForFreeDelivery}
              promoCode={promoCode}
              setPromoCode={setPromoCode}
              appliedPromo={appliedPromo}
              promoMessage={promoMessage}
              showPromoInput={showPromoInput}
              setShowPromoInput={setShowPromoInput}
              handleApplyPromoCode={handleApplyPromoCode}
              handleRemovePromo={handleRemovePromo}
            />
          </div>

          {/* We've moved the delivery schedule section above so we don't need it here */}

          {/* Proceed to Payment Button */}
          <div className="mt-8 text-center">
            <Button
              type="submit"
              disabled={isLoading || !selectedAddressId || !currentUser}
              className="w-full py-3 text-lg"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  Proceed to Payment{' '}
                  <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                </span>
              )}
            </Button>
          </div>
        </form>
      </div>
    );
  }
);

export default SubscriptionPanel;
